import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { fetchNavRegistry, resolveRequiredPerms } from "@/lib/navRegistry";
import AccessDenied from "@/pages/AccessDenied";
import ForcedPasswordChange from "@/pages/security/ForcedPasswordChange";

// P-fix (Issue 2) — strict, CONSISTENT RBAC:
//  • Route access is gated by the SAME permission model that governs sidebar visibility
//    (the nav registry). If a menu is hidden, its route is denied; if visible, its route
//    is reachable. No more menu-visible-but-route-blocked mismatch.
//  • Unauthorized access renders a proper Access Denied (403) page — never a generic
//    fallback redirect to the Executive Command Center.
export default function ProtectedRoute({ children, requireRole, requirePermission }) {
  const { status, user, hasPermission } = useAuth();
  const loc = useLocation();
  const [registry, setRegistry] = useState(undefined); // undefined = loading, [] = loaded

  useEffect(() => {
    let alive = true;
    if (status === "authed") fetchNavRegistry().then((r) => alive && setRegistry(r));
    return () => { alive = false; };
  }, [status]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">
        Loading workspace…
      </div>
    );
  }
  if (status !== "authed" || !user) {
    return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  }
  // Issue 8 — force a temporary/expired-password user to set a new password before
  // accessing ANY workspace route. Blocks the entire /app tree with a single screen.
  if (user.must_change_password) {
    return <ForcedPasswordChange />;
  }
  if (registry === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">
        Loading workspace…
      </div>
    );
  }

  // 1) Authoritative gate: the nav registry (same source as menu visibility).
  const req = resolveRequiredPerms(registry, loc.pathname);
  if (req !== null) {
    const allowed = req.length === 0 || req.some((p) => hasPermission(p));
    return allowed ? children : <AccessDenied />;
  }

  // 2) Fallback for routes not covered by the nav registry (rare sub-pages / utilities).
  // Policy: a role that lacks access never SEES the module (hidden from nav + palette). If it still
  // reaches a guarded URL via a stale/deep link, redirect quietly to its own workspace — no
  // "Access Denied" page (users should not be shown something they can't use).
  if (requirePermission) {
    const codes = Array.isArray(requirePermission) ? requirePermission : [requirePermission];
    if (!codes.some((c) => hasPermission(c))) return <Navigate to="/app" replace />;
  }
  if (requireRole && !requireRole.includes(user.role)) {
    return <Navigate to="/app" replace />;
  }
  return children;
}
